import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Tablet, Laptop, HelpCircle, ArrowLeft, ArrowRight, Camera, CheckCircle2, Loader2, MessageCircle, Upload, X } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";

const DEVICES = [
  { value: "smartphone", label: "Smartphone", icon: Smartphone },
  { value: "tablette", label: "Tablette", icon: Tablet },
  { value: "ordinateur", label: "Ordinateur", icon: Laptop },
  { value: "autre", label: "Autre appareil", icon: HelpCircle },
];

const PROBLEMS = [
  { value: "ecran", label: "Écran cassé / tactile", icon: Smartphone },
  { value: "batterie", label: "Batterie / charge", icon: Smartphone },
  { value: "camera", label: "Caméra / photos", icon: Camera },
  { value: "connecteur", label: "Connecteur de charge", icon: Smartphone },
  { value: "vitre_arriere", label: "Vitre arrière", icon: Smartphone },
  { value: "haut_parleur", label: "Haut-parleur / micro", icon: Smartphone },
  { value: "autre", label: "Autre panne", icon: HelpCircle },
];

const EXTRA_QUESTIONS = {
  ecran: ["L'écran affiche-t-il encore quelque chose ?", "Le tactile fonctionne-t-il ?"],
  batterie: ["La batterie se décharge-t-elle très vite ?", "Le téléphone s'éteint-il tout seul ?"],
  camera: ["Les photos sont-elles floues ?", "L'appareil photo refuse-t-il de s'ouvrir ?"],
  connecteur: ["Le câble tient-il mal dans le connecteur ?", "La charge est-elle intermittente ?"],
  vitre_arriere: ["La vitre arrière est-elle fissurée ?", "Les bords sont-ils abîmés ?"],
  haut_parleur: ["Le son est-il faible ou saturé ?", "Vos correspondants vous entendent-ils mal ?"],
  autre: ["La panne est-elle apparue après un choc ou un contact avec du liquide ?", "L'appareil s'allume-t-il encore ?"],
};

const STEPS = ["Appareil", "Modèle", "Panne", "Détails", "Photos", "Contact", "Estimation"];

export default function RepairQuote() {
  const { waLink, settings } = useSettings();
  const [step, setStep] = useState(0);
  const [models, setModels] = useState([]);
  const [device, setDevice] = useState("smartphone");
  const [brand, setBrand] = useState("");
  const [modelId, setModelId] = useState("");
  const [customModel, setCustomModel] = useState("");
  const [modelNotFound, setModelNotFound] = useState(false);
  const [problem, setProblem] = useState("");
  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => {
    api.get("/repair/models").then((r) => setModels(r.data)).catch(() => {});
  }, []);

  const brands = useMemo(() => [...new Set(models.map((m) => m.brand))], [models]);
  const brandModels = useMemo(() => models.filter((m) => m.brand === brand), [models, brand]);

  const modelLabel = modelNotFound ? customModel : (models.find((m) => m.id === modelId)?.name || "");

  const canNext = () => {
    if (step === 1) return modelNotFound ? customModel.trim().length > 1 : Boolean(modelId);
    if (step === 2) return Boolean(problem);
    if (step === 5) return firstName.trim().length > 0 && phone.trim().length >= 6;
    return true;
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/uploads/quote", form);
      setPhotos((p) => [...p, r.data.path].slice(0, 4));
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error(formatApiError(e, "Envoi de la photo impossible"));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/repair/requests", {
        device_type: device,
        brand: modelNotFound ? "" : brand,
        model_id: modelNotFound ? null : modelId || null,
        model_label: modelLabel,
        problem,
        answers,
        photos,
        first_name: firstName,
        phone,
      });
      setResult(r.data);
      setStep(6);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const estimateText = (est) => {
    if (!est || est.price == null) return "Devis après diagnostic";
    return est.type === "fixed" ? `${est.price} €` : `À partir de ${est.price} €`;
  };

  return (
    <div className="container-oxy max-w-2xl py-10" data-testid="repair-quote-page">
      <p className="eyebrow text-center">Réparation</p>
      <h1 className="mt-1 text-center font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Votre devis en 2 minutes</h1>

      <div className="mt-8 flex items-center justify-center gap-1.5" data-testid="repair-progress">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${i <= step ? "bg-gold w-8" : "bg-slate-200 w-4"}`} title={s} />
        ))}
      </div>
      <p className="mt-2 text-center text-xs font-semibold uppercase tracking-widest text-slate-400">
        Étape {Math.min(step + 1, 7)} / 7 — {STEPS[step]}
      </p>

      <div className="card-oxy mt-6 p-5 sm:p-8" data-testid={`repair-step-${step}`}>
        {step === 0 && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Quel appareil faut-il réparer ?</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {DEVICES.map((d) => {
                const Icon = d.icon;
                return (
                  <button key={d.value} data-testid={`device-${d.value}`} onClick={() => { setDevice(d.value); setStep(1); }}
                    className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-5 transition-all ${device === d.value ? "border-gold bg-gold-soft" : "border-slate-200 hover:border-gold/50"}`}>
                    <Icon className="h-7 w-7 text-ink" />
                    <span className="text-sm font-semibold text-slate-800">{d.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Quel modèle ?</h2>
            {!modelNotFound ? (
              <>
                <label className="label-oxy mt-5">Marque</label>
                <select data-testid="repair-brand-select" value={brand} onChange={(e) => { setBrand(e.target.value); setModelId(""); }} className="input-oxy">
                  <option value="">Choisir une marque…</option>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                {brand && (
                  <>
                    <label className="label-oxy mt-4">Modèle</label>
                    <select data-testid="repair-model-select" value={modelId} onChange={(e) => setModelId(e.target.value)} className="input-oxy">
                      <option value="">Choisir un modèle…</option>
                      {brandModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </>
                )}
                <button data-testid="repair-model-notfound" onClick={() => setModelNotFound(true)}
                  className="mt-4 text-sm font-semibold text-gold-dark hover:underline">
                  Je ne trouve pas mon modèle
                </button>
              </>
            ) : (
              <>
                <label className="label-oxy mt-5">Décrivez votre appareil</label>
                <input data-testid="repair-custom-model-input" value={customModel} onChange={(e) => setCustomModel(e.target.value)}
                  placeholder="Ex : Huawei P30, tablette Lenovo…" className="input-oxy" />
                <button onClick={() => setModelNotFound(false)} className="mt-3 text-sm font-semibold text-gold-dark hover:underline">
                  Retour à la liste des modèles
                </button>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Quel est le problème ?</h2>
            <div className="mt-5 grid gap-2.5">
              {PROBLEMS.map((p) => (
                <button key={p.value} data-testid={`problem-${p.value}`} onClick={() => setProblem(p.value)}
                  className={`flex items-center gap-3 rounded-2xl border-2 px-4 py-3.5 text-left transition-all ${problem === p.value ? "border-gold bg-gold-soft" : "border-slate-200 hover:border-gold/50"}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${problem === p.value ? "bg-gold" : "bg-slate-300"}`} />
                  <span className="font-semibold text-slate-800">{p.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Quelques précisions</h2>
            <div className="mt-5 space-y-5">
              {(EXTRA_QUESTIONS[problem] || []).map((q) => (
                <div key={q} className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-sm font-semibold text-slate-800">{q}</p>
                  <div className="mt-3 flex gap-2">
                    {["Oui", "Non", "Je ne sais pas"].map((a) => (
                      <button key={a} data-testid={`answer-${q.slice(0, 12).replace(/\W/g, "-")}-${a.toLowerCase().replace(/\W/g, "-")}`}
                        onClick={() => setAnswers((prev) => ({ ...prev, [q]: a }))}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${answers[q] === a ? "bg-ink text-gold" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Photos de la panne <span className="text-sm font-normal text-slate-400">(optionnel)</span></h2>
            <p className="mt-2 text-sm text-slate-600">Jusqu'à 4 photos pour affiner l'estimation.</p>
            <div className="mt-5 grid grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={p} className="relative aspect-square overflow-hidden rounded-xl border border-slate-200">
                  <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button data-testid={`photo-remove-${i}`} onClick={() => setPhotos(photos.filter((x) => x !== p))}
                    className="absolute right-1 top-1 rounded-full bg-ink/80 p-1 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              {photos.length < 4 && (
                <button data-testid="photo-upload-btn" onClick={() => fileInput.current?.click()} disabled={uploading}
                  className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-gold hover:text-gold-dark">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                  <span className="text-[10px] font-semibold">Ajouter</span>
                </button>
              )}
            </div>
            <input ref={fileInput} data-testid="photo-file-input" type="file" accept="image/*" className="hidden"
              onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Vos coordonnées</h2>
            <p className="mt-2 text-sm text-slate-600">Pour vous recontacter avec le tarif confirmé.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="label-oxy" htmlFor="repair-firstname">Prénom</label>
                <input id="repair-firstname" data-testid="repair-firstname-input" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Votre prénom" className="input-oxy" />
              </div>
              <div>
                <label className="label-oxy" htmlFor="repair-phone">Téléphone</label>
                <input id="repair-phone" data-testid="repair-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="06 12 34 56 78" className="input-oxy" />
              </div>
            </div>
          </div>
        )}

        {step === 6 && result && (
          <div className="text-center" data-testid="repair-result">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-soft">
              <CheckCircle2 className="h-9 w-9 text-gold-dark" />
            </span>
            <h2 className="mt-4 font-display text-2xl font-extrabold text-slate-900">Demande envoyée</h2>
            <p className="mt-1 font-mono text-lg font-bold tracking-wider text-gold-dark" data-testid="repair-reference">{result.reference}</p>

            <div className="mt-6 rounded-2xl border-2 border-gold/40 bg-gold-soft p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gold-dark">Votre estimation</p>
              <p className="mt-1 font-display text-4xl font-extrabold text-ink" data-testid="repair-estimate">{estimateText(result.estimate)}</p>
              {result.estimate.duration && (
                <p className="mt-2 text-sm font-semibold text-slate-600">Durée indicative : {result.estimate.duration}</p>
              )}
              <p className="mt-4 rounded-xl bg-white/70 p-3 text-xs leading-relaxed text-slate-500" data-testid="repair-legal-note">
                Estimation indicative : le tarif exact sera confirmé après diagnostic de votre appareil en boutique.
              </p>
            </div>

            {Boolean((settings?.whatsapp_number || "").trim()) && (
              <a
                href={waLink(`Bonjour Oxyservices, je viens de faire une demande de devis ${result.reference} (${modelLabel || device}, ${PROBLEMS.find((p) => p.value === problem)?.label}).`)}
                target="_blank" rel="noopener noreferrer" data-testid="repair-whatsapp-btn"
                className="btn-gold mt-6 w-full">
                <MessageCircle className="h-4 w-4" /> Envoyer sur WhatsApp
              </a>
            )}
            <Link to="/rendez-vous" data-testid="repair-rdv-link" className="btn-outline-oxy mt-3 w-full">Prendre rendez-vous</Link>
          </div>
        )}

        {step < 6 && (
          <div className="mt-8 flex items-center justify-between gap-3">
            <button data-testid="repair-step-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:text-ink disabled:opacity-0">
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
            {step < 5 ? (
              <button data-testid="repair-step-next" onClick={() => setStep((s) => s + 1)} disabled={!canNext()}
                className="btn-gold !px-6 !py-2.5 text-sm disabled:opacity-50">
                Continuer <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button data-testid="repair-submit-btn" onClick={submit} disabled={!canNext() || submitting}
                className="btn-gold !px-6 !py-2.5 text-sm disabled:opacity-50">
                {submitting ? "Envoi…" : "Obtenir mon estimation"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
